import type { ReactElement } from 'react';
import {
  type labRecordType,
  ProcedureRecordType,
  SourceType,
  UnifiedAdditionalRecordClassificationType,
  type ExistingPatient,
} from '@/feature/patients/types';

export type DocumentViewerPatient = Pick<
  ExistingPatient,
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'dateOfBirth'
  | 'genderName'
  | 'address'
  | 'contact'
  | 'key'
  | 'organizationProfile'
>;

export enum DocumentSource {
  MEDICAL_RECORDS = 'medicalRecords',
  MEDICAL_DATA = 'medicalData',
  PROVIDERS = 'providers',
  NON_MEDICAL = 'nonMedical',
}

export enum DocumentType {
  VITALS = 'vitals',
  LAB_RESULTS = 'labResults',
  IMAGING = 'imaging',
  PROCEDURES = 'procedures',
  PATHOLOGY_RESULTS = 'pathologyResults',
  MEDICATIONS = 'medications',
  PROBLEMS = 'problems',
  ENCOUNTERS = 'encounters',
  ADDITIONAL = 'additional',
  PROVIDER_NOTES = 'providerNotes',
  ORIGINAL_RECORDS = 'originalRecords',
  ALLERGIES = 'allergies',
  VACCINATIONS = 'vaccinations',
  NON_MEDICAL_DOCUMENT = 'nonMedicalDocument',
}

export type DocumentRegistryKey = `${DocumentSource}:${DocumentType}`;

export const documentRegistryKey = (
  source: DocumentSource,
  type: DocumentType,
): DocumentRegistryKey => `${source}:${type}`;

export type DocumentKey =
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.VITALS;
      vitalId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.VITALS;
      vitalId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.LAB_RESULTS;
      labId: string;
      sourceType: SourceType;
      recordType?: labRecordType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.LAB_RESULTS;
      labPanelId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.PROBLEMS;
      problemId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.PROBLEMS;
      problemId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.PROVIDER_NOTES;
      providerNoteId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.IMAGING;
      imagingId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.PROCEDURES;
      procedureId: string;
      sourceType: SourceType;
      recordType?: ProcedureRecordType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.PATHOLOGY_RESULTS;
      pathologyId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.MEDICATIONS;
      medicationId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.ENCOUNTERS;
      encounterId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.ADDITIONAL;
      additionalRecordId: string;
      classificationType: UnifiedAdditionalRecordClassificationType;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_RECORDS;
      type: DocumentType.ORIGINAL_RECORDS;
      originalRecordId: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.MEDICATIONS;
      medicationId?: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.PROCEDURES;
      procedureId?: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.ALLERGIES;
      allergyId?: string;
      sourceType: SourceType;
      name?: string;
    }
  | {
      source: DocumentSource.MEDICAL_DATA;
      type: DocumentType.VACCINATIONS;
      vaccinationId?: string;
      sourceType: SourceType;
      name?: string;
    };

export interface DocumentTemplateRenderContext {
  filename?: string;
  patient?: DocumentViewerPatient;
}

export enum MedicationDataType {
  MedicationsJson = 'medications_json',
  MedicationsDigitalData = 'medications_digital_data',
}

export type DocumentEntry =
  | {
      kind: 'pdf';
      signedUrl?: string;
      title?: string;
      uploadedAt?: string | Date;
      sourceRequestId?: string;
    }
  | {
      kind: 'template';
      render: (ctx?: DocumentTemplateRenderContext) => ReactElement;
      highlightAnchorId?: string;
      title?: string;
      uploadedAt?: string | Date;
      sourceRequestId?: string;
      medicationDataType?: MedicationDataType;
    };
